let allocationKey = 0;

const toDate = value => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const text = String(value).trim();
  const normalized = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2})?$/.test(text)
    ? `${text.replace(' ', 'T')}Z`
    : text;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const utcMinuteOfDay = date => (date.getUTCHours() * 60) + date.getUTCMinutes();

// Only the UTC times of day count. A typed time takes its date from whatever the field or the
// flight already held, so the STD and the off-block can sit on different days (a booking dated
// the day before, a fuel save moving date_of_flight) and subtracting the dates turned a two-minute
// delay into a day and two minutes. Up to 12 h after the STD is a delay, anything else is early.
// The backend's calculateDepartureDelayMinutes in routes/flights.js must give the same answer.
export const calculateDepartureDelayMinutes = (scheduledValue, offBlockValue) => {
  const scheduled = toDate(scheduledValue);
  const offBlock = toDate(offBlockValue);
  if (!scheduled || !offBlock) return 0;

  const difference = (utcMinuteOfDay(offBlock) - utcMinuteOfDay(scheduled) + 1440) % 1440;
  return difference <= 720 ? difference : 0;
};

export const formatDelayMinutes = value => {
  const total = Math.max(0, Math.min(999, Math.round(Number(value) || 0)));
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

export const parseDelayClock = value => {
  const rawDigits = String(value || '').replace(/\D/g, '');
  const digits = (rawDigits.replace(/^0+/, '') || (rawDigits ? '0' : '')).slice(-4);
  if (!digits) return { input: '', minutes: '' };
  const padded = digits.padStart(4, '0');
  const hours = Number.parseInt(padded.slice(0, 2), 10) || 0;
  const minutes = Math.min(Number.parseInt(padded.slice(2), 10) || 0, 59);
  const total = Math.min((hours * 60) + minutes, 999);
  return {
    input: formatDelayMinutes(total),
    minutes: String(total)
  };
};

export const finalizeDelayClock = value => {
  return parseDelayClock(value);
};

export const createDelayAllocation = source => ({
  _key: source?._key || `delay-${Date.now()}-${allocationKey += 1}`,
  id: source?.id || null,
  delay_code: String(source?.delay_code || ''),
  delay_description: String(source?.delay_description || ''),
  delay_minutes: source?.delay_minutes == null ? '' : String(source.delay_minutes),
  time_input: source?.delay_minutes == null || source?.delay_minutes === ''
    ? ''
    : formatDelayMinutes(source.delay_minutes)
});

export const getEnteredDelayMinutes = allocations => (
  (allocations || []).reduce((total, allocation) => {
    const minutes = Number(allocation?.delay_minutes);
    return total + (Number.isFinite(minutes) && minutes > 0 ? minutes : 0);
  }, 0)
);

export const ensureDelayAllocationRows = (allocations, calculatedMinutes) => {
  const calculated = Math.max(0, Number(calculatedMinutes) || 0);
  const rows = (allocations || []).map(row => row?._key ? row : createDelayAllocation(row));
  if (calculated <= 0) return rows;

  const entered = getEnteredDelayMinutes(rows);
  const hasIncompleteRow = rows.some(row => !row.delay_code || !(Number(row.delay_minutes) > 0));
  if (entered < calculated && !hasIncompleteRow) return [...rows, createDelayAllocation()];
  if (rows.length === 0) return [createDelayAllocation()];
  if (entered >= calculated) return rows.filter(row => row.delay_code || Number(row.delay_minutes) > 0);
  return rows;
};

export const prepareDelayAllocations = (delays, calculatedMinutes) => (
  ensureDelayAllocationRows((delays || []).map(createDelayAllocation), calculatedMinutes)
);

export const serializeDelayAllocations = allocations => (
  (allocations || [])
    .filter(row => row.delay_code && Number(row.delay_minutes) > 0)
    .map(row => ({
      delay_code: row.delay_code,
      delay_description: row.delay_description || null,
      delay_minutes: Number(row.delay_minutes)
    }))
);
